import { useNavigate, useParams } from "react-router-dom";
import { TripsAndPlanningsHistory } from "../../trips/tripsApi";
import {
  Badge,
  dateFormatterWeekDayDayMonthYear,
  NavigationArrows,
  RemoveRedEyeOutlined,
  SidePanelDS,
  Spacer,
  Tag,
  TextCapitalized,
} from "@qivia/ui";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import { t } from "i18next";
import styled from "styled-components";
import { useCallback, useMemo } from "react";
import { getDate } from "../../../../../libSlice/materialUiFormats";
import { DayTripPlanningDetails } from "./dayTripPlanningDetails";
import { useAppSelector } from "../../../../../redux/hooks";
import {
  selectAuthorizationsRejectedByByPeriodList,
  selectTransactionsListByPeriod,
} from "../../../transactions/transactionsSlice";

export type TripsAndPlanningsHistoryProps = {
  tripsAndPlanningsHistory: TripsAndPlanningsHistory;
  previousTripId: string | null;
  nextTripId: string | null;
};

type CurrentTripDetailsProps = {
  title: string;
  text: string | null | undefined;
  haveBadge: boolean;
  badgeBackgroundColor?: string;
};

export const CurrentTripDetails = (props: CurrentTripDetailsProps) => {
  const formattedValue = useMemo(() => {
    if (props.text) {
      return props.text;
    } else {
      return "-";
    }
  }, [props.text]);

  return (
    <StyledTitle>
      <TextCapitalized>{props.title}</TextCapitalized>
      <Spacer y={0.5} />
      {props.text ? (
        <>
          {props.haveBadge ? (
            <>
              <Badge
                text={<StyledBodyText>{formattedValue}</StyledBodyText>}
                backgroundColor={
                  props.badgeBackgroundColor ??
                  colors["colors/system/informative/informative_veryLight"]
                }
                textColor={colors["colors/text/darkGrey"]}
              />
            </>
          ) : (
            <>
              <TextCapitalized>{formattedValue}</TextCapitalized>
            </>
          )}
        </>
      ) : (
        <>
          <StyledBodyText>{"-"}</StyledBodyText>
        </>
      )}
    </StyledTitle>
  );
};

export const HistoryTripsSidePanel = (props: TripsAndPlanningsHistoryProps) => {
  const params = useParams();
  const navigate = useNavigate();
  const firstPath = location.pathname.split("/")[1];
  const transactionsListByPeriodInit = useAppSelector(
    selectTransactionsListByPeriod,
  );
  const authorizationsRejectedByByPeriodListInit = useAppSelector(
    selectAuthorizationsRejectedByByPeriodList,
  );
  const currentTripDetails = props.tripsAndPlanningsHistory;

  const transactionsAndAuthFilteredOnTripDate = useMemo(() => {
    if (!currentTripDetails) return [];

    return [
      ...(transactionsListByPeriodInit ?? []),
      ...(authorizationsRejectedByByPeriodListInit ?? []),
    ].filter((t) => t.driver === currentTripDetails.driverUuid);
  }, [
    currentTripDetails,
    transactionsListByPeriodInit,
    authorizationsRejectedByByPeriodListInit,
  ]);

  const currentDistanceVariation = useMemo(() => {
    const percentageVariation = currentTripDetails.distanceVariationPercent;
    const distanceVariation = currentTripDetails.distanceVariation;

    if (!distanceVariation || !percentageVariation) return;

    if (percentageVariation <= 0)
      return {
        text: `${distanceVariation?.toFixed(1)}`,
        backgroundColor: colors["colors/system/success/success_veryLight"],
      };
    else if (percentageVariation < 20)
      return {
        text: `${"+" + distanceVariation?.toFixed(1)}`,
        backgroundColor: colors["colors/system/warning/warning_veryLight"],
      };
    else
      return {
        text: `${"+" + distanceVariation?.toFixed(1)}`,
        backgroundColor: colors["colors/system/error/error_veryLight"],
      };
  }, [
    currentTripDetails.distanceVariation,
    currentTripDetails.distanceVariationPercent,
  ]);

  const formattedDate = useMemo(() => {
    return dateFormatterWeekDayDayMonthYear(
      getDate(currentTripDetails.date) as Date,
    );
  }, [currentTripDetails.date]);

  const onSidePanelClosed = useCallback(() => {
    navigate(`/${firstPath}/fleet/history`);
  }, [navigate, firstPath]);

  if (!currentTripDetails) {
    return;
  }

  if (params.tab !== "history" || !params.id) {
    return;
  }

  return (
    <SidePanelDS.SidePanel
      visible={true}
      onClose={onSidePanelClosed}
      withoutOverflow={true}
    >
      <StyledSidePanelContainer>
        <SidePanelDS.Section
          background={colors["colors/surfaces/background/background_level2,5"]}
          verticalSpacing={1.2}
          horizontalSpacing={1.5}
        >
          <StyledPanelNavigation>
            <StyledNavigationArrows>
              <NavigationArrows
                title={formattedDate}
                onPrevious={() =>
                  navigate(
                    `/${firstPath}/fleet/history/${props.previousTripId}`,
                  )
                }
                onNext={() =>
                  navigate(`/${firstPath}/fleet/history/${props.nextTripId}`)
                }
              />
            </StyledNavigationArrows>
            <Spacer y={1.5} />

            <StyledVehicleItem>
              <SidePanelDS.SectionedActionStripe
                left={
                  <StyledRowCardCenter>
                    {currentTripDetails.reference && (
                      <>
                        <Tag
                          textColor={colors["colors/text/darkGrey"]}
                          text={currentTripDetails.reference}
                          borderColor={colors["colors/text/darkGrey"]}
                        />
                        <Spacer x={2} />
                      </>
                    )}
                    <StyledVehicleResume>
                      {currentTripDetails.registrationNumber}
                    </StyledVehicleResume>
                    <Spacer x={1} />
                  </StyledRowCardCenter>
                }
                right={
                  <StyledLittleIcon>
                    <RemoveRedEyeOutlined
                      key="vehicle"
                      onClick={() =>
                        navigate(
                          `/${firstPath}/vehicles/${currentTripDetails.vehicleUuid}?fromNavigate=true`,
                        )
                      }
                    />
                    <Spacer x={0.5} />
                  </StyledLittleIcon>
                }
              />
            </StyledVehicleItem>
          </StyledPanelNavigation>
        </SidePanelDS.Section>

        <SidePanelDS.Section verticalSpacing={2} horizontalSpacing={1.5}>
          <SectionTitle>
            <TextCapitalized>
              {t("trips.details.sidePanel.title")}
            </TextCapitalized>
          </SectionTitle>
          <Spacer y={2} />
          <StyledRowCenter>
            <>
              <CurrentTripDetails
                haveBadge={true}
                title={t("trips.details.sidePanel.vehicleDistance")}
                text={currentTripDetails.totalDistanceInKm?.toFixed(1) + " km"}
                badgeBackgroundColor={
                  colors["colors/system/informative/informative_veryLight"]
                }
              />
              <Spacer y={2} />
            </>
            {currentTripDetails.associatedPlanning && (
              <>
                <Spacer x={1.5} />
                <CurrentTripDetails
                  haveBadge={true}
                  title={t("trips.details.sidePanel.planningVehicleDistance")}
                  text={
                    currentTripDetails.associatedPlanning.expectedMileagePerDay.toFixed(
                      1,
                    ) + " km"
                  }
                  badgeBackgroundColor={
                    colors["colors/system/informative/informative_veryLight"]
                  }
                />
                <Spacer x={1.5} />
                <CurrentTripDetails
                  haveBadge={true}
                  title={t("trips.details.sidePanel.vehicleDistanceVariation")}
                  text={currentDistanceVariation?.text + " km"}
                  badgeBackgroundColor={
                    currentDistanceVariation?.backgroundColor
                  }
                />
              </>
            )}
            <Spacer x={1.5} />
            <CurrentTripDetails
              haveBadge={false}
              title={t("trips.details.sidePanel.driverCity")}
              text={currentTripDetails.driverCity?.toLocaleUpperCase()}
              badgeBackgroundColor={
                colors["colors/system/informative/informative_veryLight"]
              }
            />
          </StyledRowCenter>
        </SidePanelDS.Section>
        <StyledHoursBarSection>
          <Spacer x={1.5} />
          <StyledColumn>
            <Spacer y={2.5} />
            <DayTripPlanningDetails
              driverName={currentTripDetails.driverName}
              trips={currentTripDetails.trips}
              plannings={currentTripDetails.associatedPlanning?.trips ?? []}
              transactionsAndAuth={transactionsAndAuthFilteredOnTripDate}
            />
            <Spacer y={1.5} />
          </StyledColumn>
          <Spacer x={1.5} />
        </StyledHoursBarSection>
      </StyledSidePanelContainer>
    </SidePanelDS.SidePanel>
  );
};

const StyledPanelNavigation = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const StyledNavigationArrows = styled.div`
  width: 80%;
  z-index: 2;
`;

const StyledSidePanelContainer = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;

  & > *:not(:first-child) {
    border-top: 0.0625rem solid ${colors["colors/borders/cards/cards"]};
  }
`;

const StyledHoursBarSection = styled.div`
  min-height: 0;
  display: flex;
`;

const StyledColumn = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const StyledRowCardCenter = styled.div`
  ${typographies["Body/S"]}
  display: flex;
  justify-content: flex-start;
  align-items: center;
  white-space: nowrap;
  background-color: ${colors["colors/surfaces/background/background_level0"]};
`;

const StyledRowCenter = styled.div`
  ${typographies["Body/S"]}
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  align-items: flex-start;
  white-space: nowrap;
  row-gap: 1rem;
`;

const StyledLittleIcon = styled.div`
  display: flex;
  cursor: pointer;
  & svg {
    width: 1rem;
    height: 1rem;
  }
`;

const StyledVehicleResume = styled.span`
  ${typographies["Body/XS"]};
  color: ${colors["colors/text/black"]};
  text-align: center;
`;

const StyledVehicleItem = styled.div`
  border: 0.025rem solid ${colors["colors/text/veryLightGrey"]};
  border-radius: 1rem;
  width: 100%;
  background-color: ${colors["colors/surfaces/background/background_level0"]};
`;

const StyledBodyText = styled.div`
  ${typographies["Body/XXS"]};
`;

const SectionTitle = styled.div`
  ${typographies["Body/L"]}
  color: ${colors["colors/text/darkGrey"]}
`;

const StyledTitle = styled.div`
  ${typographies["Body/S"]};
  display: flex;
  flex-direction: column;
  align-items: start;
`;
